import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadRequestError } from '@dvag/dfs-api-client';
import type { CustomError } from '@dvag/dfs-api-client';
import { useNotification } from '../utils/notification/NotificationContext';
import { familyApprovalApi } from '../service/api/familyApprovalApi';
import { MultipleFamilyApprovalType } from '../type/twoFactor';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { consultationFamilyApprovalApi } from '../service/api/consultationFamilyApprovalApi';
import { permissions } from '@dvag/dfs-constant-config';

export const useFamilyApprovalPersonCentric = () => {
  const { showError } = useNotification();
  const queryClient = useQueryClient();
  const isConsultationServiceEnabled = useFeatureIsOn(permissions.canAccessConsultationService);
  const familyApprovalService = isConsultationServiceEnabled
    ? consultationFamilyApprovalApi()
    : familyApprovalApi();

  return useMutation<
    Awaited<ReturnType<typeof familyApprovalService.sendMultipleFamilyApproval>>,
    CustomError,
    MultipleFamilyApprovalType,
    unknown
  >({
    mutationFn: async (payload: MultipleFamilyApprovalType) => {
      if (!payload.data || payload.data.length === 0) {
        throw new BadRequestError('No person approval data provided');
      }

      if (!payload.householdId || !payload.customerPersonId) {
        throw new BadRequestError('Invalid householdId or customerPersonId');
      }

      return familyApprovalService.sendMultipleFamilyApproval(payload);
    },
    onSuccess: async (_payloadSuccess, payload) => {
      await queryClient.invalidateQueries({
        queryKey: [
          `familyApproval-person-centric-${payload.householdId}-${payload.customerPersonId}`,
        ],
      });
    },
    onError: (err: CustomError) => {
      showError({ status: err?.status });
    },
  });
};
